import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRightLeft, Car, GitBranch, Truck, User, UserRound, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { RefLabel, clearLookups } from "@/lib/lookups";
import { errMsg, fmtDateTime } from "@/lib/format";
import { MASTERS } from "@/lib/masters";
import { FieldInput } from "@/components/master/FieldInput";
import { CellValue } from "@/components/master/MasterList";
import { EmptyState, ErrorState, LoadingRows, Field, Badge } from "@/components/common/States";

const ActionDialog = ({ action, onClose, onDone }) => {
  const { api } = useSession();
  const [value, setValue] = useState(action?.initial ?? null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!action) return null;
  const submit = async () => {
    if (action.reasonRequired && reason.trim().length < 3) return toast.error("Please give a reason (min 3 characters)");
    if (action.valueRequired && !value) return toast.error(`Select ${action.field.label.toLowerCase()}`);
    setBusy(true);
    try {
      await api.post(action.url, { [action.key]: value, reason: reason || null });
      clearLookups();
      toast.success(action.done);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="relation-action-dialog">
        <DialogHeader><DialogTitle>{action.title}</DialogTitle><DialogDescription>{action.description}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {action.field && <Field label={action.field.label}><FieldInput field={action.field} value={value} onChange={setValue} /></Field>}
          <Field label={`Reason${action.reasonRequired ? " *" : ""}`}><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="relation-reason-input" /></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={submit} data-testid="relation-confirm-button">{busy ? "Saving…" : "Confirm"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HISTORY_LABEL = { branch_transfer: "Branch transfer", owner_change: "Owner change", fleet_assign: "Added to fleet", fleet_reassign: "Moved to another fleet", fleet_unassign: "Removed from fleet", driver_assign: "Driver assigned", driver_reassign: "Driver reassigned", driver_unassign: "Driver unassigned" };
const REF_OF = { owner_change: "customers", fleet_assign: "fleets", fleet_reassign: "fleets", fleet_unassign: "fleets", driver_assign: "drivers", driver_reassign: "drivers", driver_unassign: "drivers" };

const BranchName = ({ id }) => {
  const { branches } = useSession();
  return <span>{branches.find((b) => b.id === id)?.name || "Branch outside your access"}</span>;
};
const Ref = ({ type, id }) => (type === "branch_transfer" ? <BranchName id={id} /> : <RefLabel kind={REF_OF[type]} id={id} />);

const HistoryList = ({ url, testId }) => {
  const { data, loading, error, reload } = useFetch(url);
  if (loading && !data) return <LoadingRows rows={2} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data.length) return <EmptyState title="No assignment history yet" testId={`${testId}-empty`} />;
  return (
    <ol className="divide-y" data-testid={testId}>
      {data.map((h) => (
        <li key={h.id} className="px-4 py-3 text-sm">
          <div className="flex flex-wrap items-center gap-2"><Badge tone="gold">{HISTORY_LABEL[h.type] || h.type}</Badge>
            <span className="text-muted-foreground">{h.from ? <Ref type={h.type} id={h.from} /> : "—"}</span><ArrowRightLeft className="h-3 w-3" /><span>{h.to ? <Ref type={h.type} id={h.to} /> : "—"}</span>
            {h.registration_number && <span className="font-mono-g text-xs text-muted-foreground">{h.registration_number}</span>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">{h.by_name} · {fmtDateTime(h.at)}{h.reason ? ` · ${h.reason}` : ""}</div>
        </li>
      ))}
    </ol>
  );
};

const RelationCard = ({ icon: Icon, label, children, action, testId }) => (
  <div className="g39-card p-4 flex items-start gap-3" data-testid={testId}>
    <div className="h-9 w-9 rounded-md bg-accent flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-gold" /></div>
    <div className="flex-1 min-w-0"><div className="g39-label">{label}</div><div className="text-sm mt-1 truncate">{children}</div></div>
    {action}
  </div>
);

const VehicleRelations = ({ record, reload }) => {
  const { can } = useSession();
  const [action, setAction] = useState(null);
  const [hv, setHv] = useState(0);
  const base = `/vehicles/${record.id}`;
  const btn = (label, a, testId, perm) => can(perm) && record.status === "active" && <Button size="sm" variant="outline" onClick={() => setAction(a)} data-testid={testId}>{label}</Button>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <RelationCard icon={GitBranch} label="Branch" testId="relation-branch" action={btn("Transfer", { title: "Transfer to another branch", description: "The previous branch is kept in history.", url: `${base}/transfer`, key: "to_branch_id", field: { name: "to_branch_id", label: "Destination branch", type: "branch", required: true }, reasonRequired: true, valueRequired: true, done: "Vehicle transferred" }, "transfer-branch-button", "vehicle.transfer")}><BranchName id={record.branch_id} /></RelationCard>
        <RelationCard icon={UserRound} label="Owner" testId="relation-owner" action={btn("Change", { title: "Change vehicle owner", description: "Ownership changes are preserved in history.", url: `${base}/owner`, key: "owner_customer_id", initial: record.owner_customer_id, field: { name: "owner_customer_id", label: "New owner", type: "ref", ref: "customers" }, reasonRequired: true, done: "Owner updated" }, "change-owner-button", "vehicle.transfer")}><RefLabel kind="customers" id={record.owner_customer_id} /></RelationCard>
        <RelationCard icon={Truck} label="Fleet" testId="relation-fleet" action={btn(record.fleet_id ? "Reassign" : "Assign", { title: "Fleet assignment", description: "Choose 'None' to remove from the fleet.", url: `${base}/fleet`, key: "fleet_id", initial: record.fleet_id, field: { name: "fleet_id", label: "Fleet", type: "ref", ref: "fleets" }, done: "Fleet assignment updated" }, "assign-fleet-button", "vehicle.manage")}><RefLabel kind="fleets" id={record.fleet_id} /></RelationCard>
        <RelationCard icon={User} label="Assigned driver" testId="relation-driver" action={can("driver.view") && btn(record.driver_id ? "Change" : "Assign", { title: "Driver assignment", description: "A driver can hold one vehicle at a time and must be permitted at this branch. Choose 'None' to unassign.", url: `${base}/driver`, key: "driver_id", initial: record.driver_id, field: { name: "driver_id", label: "Driver", type: "ref", ref: "drivers" }, done: "Driver assignment updated" }, "assign-driver-button", "vehicle.manage")}><RefLabel kind="drivers" id={record.driver_id} /></RelationCard>
      </div>
      <div className="g39-card"><div className="px-4 py-3 border-b font-display font-semibold">Assignment & ownership history</div><HistoryList key={hv} url={`${base}/history`} testId="vehicle-history" /></div>
      <div className="g39-card g39-stripes p-4 text-sm text-muted-foreground" data-testid="vehicle-service-history-placeholder">Service history, downtime and lifetime maintenance cost appear here once job cards are implemented (Phase 3). No figures are shown until real records exist.</div>
      <ActionDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); reload(); setHv((x) => x + 1); }} />
    </div>
  );
};

const LinkedTable = ({ kind, params, empty, testId, action }) => {
  const cfg = MASTERS[kind];
  const { data, loading, error, reload } = useFetch(`/${kind}`, { limit: 50, status: "all", ...params });
  if (loading && !data) return <LoadingRows rows={2} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data.items.length) return <EmptyState title={empty} testId={`${testId}-empty`} />;
  return (
    <div className="divide-y" data-testid={testId}>
      {data.items.map((d) => (
        <div key={d.id} className="flex items-center gap-3 px-4 py-3 text-sm">
          <Link to={`${cfg.path}/${d.id}`} className="flex-1 min-w-0 hover:text-gold"><CellValue col={cfg.columns[0]} d={d} /></Link>
          <Badge tone={d.status}>{d.status}</Badge>
          {action && action(d, reload)}
        </div>
      ))}
      {data.total > 50 && <div className="px-4 py-2 text-xs text-muted-foreground">Showing 50 of {data.total}</div>}
    </div>
  );
};

const CustomerRelations = ({ record }) => (
  <div className="grid lg:grid-cols-2 gap-5">
    <div className="g39-card"><div className="px-4 py-3 border-b font-display font-semibold flex items-center gap-2"><Car className="h-4 w-4 text-gold" />Linked vehicles</div>
      <LinkedTable kind="vehicles" params={{ related_customer_id: record.id }} empty="No vehicles linked" testId="customer-vehicles" /></div>
    <div className="space-y-5">
      <div className="g39-card"><div className="px-4 py-3 border-b font-display font-semibold flex items-center gap-2"><Truck className="h-4 w-4 text-gold" />Fleets owned</div>
        <LinkedTable kind="fleets" params={{ owner_customer_id: record.id }} empty="No fleets owned" testId="customer-fleets" /></div>
      <div className="g39-card g39-stripes p-4 text-sm" data-testid="customer-account-placeholder">
        <div className="font-display font-semibold mb-1">Account & outstanding balance</div>
        <p className="text-muted-foreground">Not tracked yet — balances, invoices and service history will be calculated from real records once billing (Phase 5) and job cards (Phase 3) are live.</p>
      </div>
    </div>
  </div>
);

const FleetRelations = ({ record }) => {
  const { can } = useSession();
  const [action, setAction] = useState(null);
  const [v, setV] = useState(0);
  const remove = (d, reload) => can("vehicle.manage") && (
    <Button size="icon" variant="ghost" title="Remove from fleet" onClick={() => setAction({ title: `Remove ${d.registration_number} from fleet`, description: "The vehicle record stays; only the fleet link is removed.", url: `/vehicles/${d.id}/fleet`, key: "fleet_id", done: "Removed from fleet" })} data-testid={`remove-from-fleet-${d.id}`}><X className="h-4 w-4" /></Button>
  );
  return (
    <div className="g39-card">
      <div className="px-4 py-3 border-b flex items-center justify-between"><span className="font-display font-semibold">Vehicles in this fleet</span>
        {can("vehicle.manage") && record.status === "active" && <AddToFleet fleetId={record.id} onDone={() => setV((x) => x + 1)} />}</div>
      <LinkedTable key={v} kind="vehicles" params={{ fleet_id: record.id }} empty="No vehicles in this fleet" testId="fleet-vehicles" action={remove} />
      <ActionDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); setV((x) => x + 1); }} />
    </div>
  );
};

const AddToFleet = ({ fleetId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [vid, setVid] = useState(null);
  const { api } = useSession();
  const go = async () => {
    if (!vid) return toast.error("Select a vehicle");
    try { await api.post(`/vehicles/${vid}/fleet`, { fleet_id: fleetId }); clearLookups(); toast.success("Vehicle added to fleet"); setOpen(false); setVid(null); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-vehicle-to-fleet-button">Add vehicle</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add an existing vehicle</DialogTitle><DialogDescription>Links the vehicle master record — nothing is copied. A vehicle in another fleet is moved and the move is recorded.</DialogDescription></DialogHeader>
          <Field label="Vehicle"><FieldInput field={{ name: "fleet_vehicle", type: "ref", ref: "vehicles" }} value={vid} onChange={setVid} /></Field>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={go} data-testid="add-vehicle-to-fleet-confirm">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DriverRelations = ({ record }) => (
  <div className="space-y-5">
    <RelationCard icon={Car} label="Current vehicle" testId="driver-current-vehicle">
      {record.current_vehicle_id ? <Link className="hover:text-gold" to={`/vehicles/${record.current_vehicle_id}`}><RefLabel kind="vehicles" id={record.current_vehicle_id} /></Link> : "Not assigned — assign from the vehicle's Assignments tab"}
    </RelationCard>
    {record.licence_expired && <div className="g39-card p-4 text-sm text-red-700 border-red-200 bg-red-50" data-testid="licence-expired-warning">Driving licence has expired. New vehicle assignments are blocked.</div>}
    <div className="g39-card"><div className="px-4 py-3 border-b font-display font-semibold">Vehicle assignment history</div><HistoryList url={`/drivers/${record.id}/history`} testId="driver-history" /></div>
  </div>
);

const ServiceRequestRelations = ({ record }) => {
  const { can } = useSession();
  const { data } = useFetch("/quotations", { service_request_id: record.id });
  return (
    <div className="g39-card" data-testid="sr-quotations">
      <div className="px-4 py-3 border-b flex items-center justify-between"><span className="font-display font-semibold">Quotations</span>
        {can("quotation.manage") && ["open", "quoted"].includes(record.stage) && <Link to={`/quotations/new?sr=${record.id}`}><Button size="sm" data-testid="create-quotation-button">Create quotation</Button></Link>}</div>
      <div className="divide-y">{(data?.items || []).map((q) => <Link key={q.id} to={`/quotations/${q.id}`} className="flex gap-3 px-4 py-3 text-sm hover:bg-secondary/40"><span className="font-mono-g">{q.number} r{q.revision}</span><Badge>{q.status}</Badge><span className="ml-auto">₹{q.totals.grand_total}</span></Link>)}
        {data && !data.items.length && <EmptyState title="No quotations yet" />}</div>
    </div>
  );
};

export const EXTRAS = {
  "service-requests": { tab: "Quotations", Component: ServiceRequestRelations },
  vehicles: { tab: "Assignments", Component: VehicleRelations },
  customers: { tab: "Vehicles & account", Component: CustomerRelations },
  fleets: { tab: "Vehicles", Component: FleetRelations },
  drivers: { tab: "Vehicle assignment", Component: DriverRelations },
};
